import React, { useState, useRef, forwardRef, useImperativeHandle } from 'react';
import axiosInstance from '../../api/axiosConfig';
import { CARD_BRANDS, cardLabel, cardExpiry, costCentreGroups } from './paymentConstants';

// The two pickers every Payments form shares: which card, and which
// company/branch the cost is charged to. One implementation each, so the
// service form, the record-payment modal and the edit-payment modal can never
// offer different choices.

const emptyCard = { brand: 'visa', last4: '', nickname: '', holderName: '', holderEmail: '', expiryMonth: '', expiryYear: '' };

// Last-4 + nickname + expiry, posted straight to the card list. Lives inside
// the picker so adding a new card never means leaving the form being filled.
// `submit()` on the ref lets the surrounding form add a half-filled card when
// it saves, instead of silently dropping it.
export const QuickCardForm = forwardRef(({ onCreated, onCancel }, ref) => {
    const [form, setForm] = useState(emptyCard);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    // Resolves to the created card, or null when it could not be added (the
    // reason is shown in the form)
    const save = async () => {
        setError('');
        if (!/^\d{4}$/.test(form.last4)) {
            setError('Enter the last 4 digits only');
            return null;
        }
        setSaving(true);
        try {
            const res = await axiosInstance.post('/api/subscriptions/cards', {
                ...form,
                expiryMonth: form.expiryMonth || null,
                expiryYear: form.expiryYear || null
            });
            const card = res.data.data;
            onCreated(card);
            return card;
        } catch (err) {
            setError(err.response?.data?.message || 'Could not add the card');
            return null;
        } finally {
            setSaving(false);
        }
    };

    // Brand and expiry selects alone are not "something typed"
    const isBlank = () => !form.last4 && !form.nickname.trim() && !form.holderName.trim() && !form.holderEmail.trim();

    useImperativeHandle(ref, () => ({ submit: save, isBlank }));

    const thisYear = new Date().getFullYear();

    return (
        // Enter must add the card, not submit the form this sits inside
        <div className="quick-card-form" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}>
            <div className="quick-card-grid">
                <select className="form-select" value={form.brand} onChange={(e) => set('brand', e.target.value)} aria-label="Card brand">
                    {CARD_BRANDS.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
                <input
                    className="form-input"
                    value={form.last4}
                    onChange={(e) => set('last4', e.target.value.replace(/\D/g, '').slice(0, 4))}
                    placeholder="Last 4"
                    inputMode="numeric"
                    aria-label="Last 4 digits"
                    autoFocus
                />
                <div className="quick-card-expiry">
                    <select className="form-select" value={form.expiryMonth} onChange={(e) => set('expiryMonth', e.target.value)} aria-label="Expiry month">
                        <option value="">MM</option>
                        {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                            <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
                        ))}
                    </select>
                    <select className="form-select" value={form.expiryYear} onChange={(e) => set('expiryYear', e.target.value)} aria-label="Expiry year">
                        <option value="">YYYY</option>
                        {Array.from({ length: 12 }, (_, i) => thisYear + i).map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </div>
                <input
                    className="form-input"
                    value={form.nickname}
                    onChange={(e) => set('nickname', e.target.value)}
                    placeholder="Nickname"
                    aria-label="Card nickname"
                />
                <input
                    className="form-input"
                    value={form.holderName}
                    onChange={(e) => set('holderName', e.target.value)}
                    placeholder="Holder name"
                    aria-label="Card holder name"
                />
                <input
                    className="form-input"
                    type="email"
                    value={form.holderEmail}
                    onChange={(e) => set('holderEmail', e.target.value)}
                    placeholder="Holder email"
                    aria-label="Card holder email"
                />
            </div>
            <div className="form-hint">
                🔒 Only the last 4 digits are stored.
            </div>
            {error && <div className="alert alert-error">{error}</div>}
            <div className="quick-card-actions">
                <button type="button" className="btn btn-sm btn-secondary" onClick={onCancel}>Cancel</button>
                <button type="button" className="btn btn-sm btn-primary" onClick={save} disabled={saving}>
                    {saving ? 'Adding...' : 'Add card'}
                </button>
            </div>
        </div>
    );
});

// value: card id, or '' for "no card / other method"
//
// The ref's `commitPending()` is for the form's submit: an open add-card form
// is easy to miss, and saving straight past it used to drop the card without a
// word. It resolves to undefined (nothing pending), the new card's id, or null
// when the card could not be added and the save should stop.
export const CardPicker = forwardRef(({ cards = [], value, onChange, onCardCreated, canAddCard = true, otherLabel = 'Other method' }, ref) => {
    const [adding, setAdding] = useState(false);
    const quickRef = useRef(null);

    useImperativeHandle(ref, () => ({
        commitPending: async () => {
            if (!adding || !quickRef.current) return undefined;
            if (quickRef.current.isBlank()) {
                setAdding(false);
                return undefined;
            }
            const card = await quickRef.current.submit();
            return card ? String(card._id) : null;
        }
    }), [adding]);
    const selected = String(value || '');
    // Offer active cards, plus the current one even if it was deactivated since
    const offered = cards.filter(c => c.isActive || String(c._id) === selected);
    const selectedCard = offered.find(c => String(c._id) === selected);
    const expiry = cardExpiry(selectedCard);

    return (
        <div className="card-picker">
            <div className="chip-row chip-row-tight">
                {offered.map(card => {
                    const exp = cardExpiry(card);
                    return (
                        <button
                            key={card._id}
                            type="button"
                            className={`chip card-chip ${selected === String(card._id) ? 'chip-active' : ''} ${exp?.expired ? 'card-chip-expired' : ''}`}
                            onClick={() => onChange(String(card._id))}
                            title={[card.holderName, card.bankName, exp ? `expires ${exp.text}` : ''].filter(Boolean).join(' · ')}
                        >
                            💳 {cardLabel(card)}
                            {!card.isActive && ' (inactive)'}
                        </button>
                    );
                })}
                <button
                    type="button"
                    className={`chip ${selected === '' ? 'chip-active' : ''}`}
                    onClick={() => onChange('')}
                >
                    {otherLabel}
                </button>
                {canAddCard && !adding && (
                    <button type="button" className="chip chip-dashed" onClick={() => setAdding(true)}>
                        + Add card
                    </button>
                )}
            </div>
            {expiry?.expired && (
                <div className="form-hint pay-text-danger">⚠ This card expired {expiry.text} — charges to it will fail.</div>
            )}
            {expiry?.soon && (
                <div className="form-hint pay-text-warning">⚠ This card expires {expiry.text}.</div>
            )}
            {offered.length === 0 && !adding && (
                <div className="form-hint">No company cards yet — add the one you pay with, it takes ten seconds.</div>
            )}
            {adding && (
                <QuickCardForm
                    ref={quickRef}
                    onCancel={() => setAdding(false)}
                    onCreated={(card) => {
                        setAdding(false);
                        onCardCreated?.(card);
                        onChange(String(card._id));
                    }}
                />
            )}
        </div>
    );
});

// value: a Tenant id (company or branch), or '' for "not set"
export const CostCentreSelect = ({ costCentres = [], value, onChange, className = 'form-select', allowEmpty = true, emptyLabel = '— Not set —' }) => {
    const groups = costCentreGroups(costCentres);
    return (
        <select className={className} value={value || ''} onChange={(e) => onChange(e.target.value)}>
            {allowEmpty && <option value="">{emptyLabel}</option>}
            {groups.map(({ company, branches }) => (
                <optgroup key={company._id} label={company.name}>
                    <option value={company._id}>{company.name} — whole company</option>
                    {branches.map(branch => (
                        <option key={branch._id} value={branch._id}>{branch.name}</option>
                    ))}
                </optgroup>
            ))}
        </select>
    );
};
